/* Document list table for displaying previously generated invoices or quotes
   Updated: Fixed status dropdown using fixed positioning to avoid overflow clipping
   Updated: per-document "Versturen" / "Opnieuw versturen" action that opens the exact-preview send dialog
   (same number; paid invoices are sent as paid). */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DocumentSendDialog from './DocumentSendDialog';
import { Mail, Send, Clock, CircleCheck, CircleDashed, TriangleAlert as AlertTriangle, ChevronDown } from 'lucide-react';

interface Document {
  id: string;
  document_number: string;
  document_type: string;
  customer_name: string;
  customer_email: string;
  total: number;
  status: string;
  created_at: string;
  sent_at: string | null;
}

interface DocumentListProps {
  documents: Document[];
  loading: boolean;
  onStatusChange?: (id: string, newStatus: string) => Promise<void>;
  onSent?: () => void;
}

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive'; icon: any; className: string }> = {
  draft: { label: 'Concept', variant: 'secondary', icon: CircleDashed, className: '' },
  sent: { label: 'Verzonden', variant: 'default', icon: Send, className: 'bg-[#e7e1d6] text-[#5a6552] border-[#cfcac2] hover:bg-[#dcd8ce]' },
  paid: { label: 'Betaald', variant: 'default', icon: CircleCheck, className: 'bg-[#e7e1d6] text-[#1e2b22] border-[#1e2b22]/30 hover:bg-[#dcd8ce]' },
  expired: { label: 'Verlopen', variant: 'destructive', icon: AlertTriangle, className: '' },
};

const allStatuses = ['draft', 'sent', 'paid', 'expired'];

function StatusDropdown({ docId, currentStatus, onStatusChange }: {
  docId: string;
  currentStatus: string;
  onStatusChange?: (id: string, newStatus: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const config = statusConfig[currentStatus] || statusConfig.draft;
  const StatusIcon = config.icon;

  const updatePosition = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left + rect.width / 2 - 70,
      });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('scroll', () => setOpen(false), true);
    return () => window.removeEventListener('scroll', () => setOpen(false), true);
  }, [open, updatePosition]);

  const handleSelect = async (newStatus: string) => {
    if (newStatus === currentStatus || !onStatusChange) return;
    setUpdating(true);
    setOpen(false);
    await onStatusChange(docId, newStatus);
    setUpdating(false);
  };

  if (!onStatusChange) {
    return (
      <Badge variant={config.variant} className={`text-[10px] ${config.className}`}>
        <StatusIcon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  }

  return (
    <div className="inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(!open)}
        disabled={updating}
        className="focus:outline-none"
      >
        <Badge
          variant={config.variant}
          className={`text-[10px] cursor-pointer select-none transition-all ${config.className} ${updating ? 'opacity-50' : ''}`}
        >
          <StatusIcon className="w-3 h-3 mr-1" />
          {config.label}
          <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${open ? 'rotate-180' : ''}`} />
        </Badge>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px]"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            {allStatuses.map((s) => {
              const sc = statusConfig[s];
              const Icon = sc.icon;
              const isActive = s === currentStatus;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleSelect(s)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                    isActive
                      ? 'bg-gray-100 font-semibold text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {sc.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default function DocumentList({ documents, loading, onStatusChange, onSent }: DocumentListProps) {
  const [sendDoc, setSendDoc] = useState<Document | null>(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jv-primary" />
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Nog geen documenten aangemaakt</p>
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Nummer</th>
                <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Klant</th>
                <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">E-mail</th>
                <th className="text-right text-xs font-semibold text-gray-600 px-4 py-3">Totaal</th>
                <th className="text-center text-xs font-semibold text-gray-600 px-4 py-3">Status</th>
                <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Datum</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id} className="border-b last:border-b-0 hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-sm font-mono font-semibold text-gray-900">{doc.document_number}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900">{doc.customer_name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-500">{doc.customer_email}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-semibold text-gray-900">
                      &euro;{parseFloat(doc.total.toString()).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusDropdown
                      docId={doc.id}
                      currentStatus={doc.status}
                      onStatusChange={onStatusChange}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-500">
                      {new Date(doc.created_at).toLocaleDateString('nl-NL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" size="sm" onClick={() => setSendDoc(doc)} className="text-xs">
                      <Mail className="w-3.5 h-3.5 mr-1.5" />
                      {doc.status === 'draft' ? 'Versturen' : 'Opnieuw versturen'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
      <DocumentSendDialog
        documentId={sendDoc?.id ?? null}
        resend={Boolean(sendDoc && sendDoc.status !== 'draft')}
        onClose={() => setSendDoc(null)}
        onSent={onSent}
      />
    </Card>
  );
}
